// Assignment Completion by Business Unit report constants (ADR-093)

// Rate banding for the report's % column and the dashboard card:
// red < 30, amber 30–59, green >= 60. LAGGING mirrors the backend's
// LAGGING_RATE_THRESHOLD (assignmentCompletionReport.service.js) — the
// card's "Review Lagging" action deep-links with belowRate = LAGGING.
export const COMPLETION_RATE_THRESHOLDS = {
  CRITICAL: 30,
  LAGGING: 60,
};

export const getCompletionRateColor = (rate) => {
  if (rate === null || rate === undefined) {
    return '#94A3B8'; // no expectation — neutral slate
  }
  if (rate < COMPLETION_RATE_THRESHOLDS.CRITICAL) {
    return '#DC2626'; // red
  }
  if (rate < COMPLETION_RATE_THRESHOLDS.LAGGING) {
    return '#F59E0B'; // amber
  }
  return '#16A34A'; // green
};

// Bar colors mirroring the product mock: sky blue for created-but-unfinished
// work, light green for completed counts, dark green for the % meter.
export const BAR_COLORS = {
  NOT_COMPLETED: '#38BDF8',
  COMPLETED: '#84CC16',
  RATE: '#166534',
  TRACK: '#EEF2F6',
};

// Assignment kind facet. "Individual" matches what the app already calls an
// unattached assignment ("Individual Assessment").
export const ASSIGNMENT_KIND_OPTIONS = [
  { value: 'all', label: 'All Kinds' },
  { value: 'session', label: 'Feedback Session' },
  { value: 'individual', label: 'Individual' },
];

// "Below target" lens filter — narrows visible rows; header stats stay whole-scope.
export const BELOW_RATE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 75, label: 'Below 75%' },
  { value: 60, label: 'Below 60%' },
  { value: 50, label: 'Below 50%' },
  { value: 30, label: 'Below 30%' },
];

// The report period is the YEAR (ADR-093 amendment — month dropped). The
// current year is rendered year-to-date server-side.

/**
 * Column explanations, shown on the grid headers AND the matching header
 * stats (same six concepts — one definition, two surfaces).
 *
 * Two rules from the ADR-014 §10i tooltip convention apply here:
 *   1. never restate the label ("Completed → the completed ones" teaches
 *      nothing) — state the CONDITION the number counts;
 *   2. every member of the vocabulary gets one, or none do.
 *
 * The tenure threshold and the expectation rule are read from the response's
 * `policy` block rather than hardcoded, so the copy can't drift when the
 * backend policy changes (e.g. per-cluster expectations).
 *
 * @param {Object} [policy] - { minTenureMonths, expectation } echoed by the API
 */
export const getColumnDescriptions = (policy = {}) => {
  const tenure = policy.minTenureMonths
    ? `at least ${policy.minTenureMonths} months`
    : 'long enough to qualify';
  // ADR-108: the year's excluded engagement models, named so the copy states
  // the real rule. Empty → today's sentences, unchanged.
  const excludedModels = policy.excludedEngagementModels || [];
  const excludedNames = formatNames(excludedModels.map((model) => model.name));
  const exclusionClause = excludedModels.length > 0
    ? ` Consultants on an engagement model excluded for the year (${excludedNames}) are also left out, whatever their tenure.`
    : '';
  const skippedSentence = excludedModels.length > 0
    ? `Allocated here but not measured — new joiners in the organization under ${policy.minTenureMonths || 3} months, and consultants on an excluded engagement model (${excludedNames}).`
    : `New joiners allocated here but too new to measure — in the organization under ${policy.minTenureMonths || 3} months.`;

  return {
    // Labelled "Measured" in the grid (renamed from "Individuals" 2026-08-11,
    // when the header gained an Individuals band over Measured + Skipped).
    // It is NOT the business unit's headcount — say so, because that is the
    // reading the old label invited.
    eligible: `The individuals this report measures: allocated full-time to this business unit's projects at any point in the year, in the organization ${tenure}, and not yet departed.${exclusionClause} Anyone else is left out of every column here, their assignments included, so the ratio stays honest. Not a headcount: the people left out are counted under Skipped, except leavers, who appear in neither.`,
    expected: 'The feedback work each cluster asks of its people for the year, set in Assignment Completion Policies and split between sequential and parallel. It stays the denominator of % Completed even when nothing was created; a cluster with no policy expects nothing and shows "—".',
    // Labelled "Open" in the grid (renamed from "Not Completed" 2026-08-11):
    // the point of this column against Missing is that the assignment EXISTS,
    // and two negatives side by side hid that. Keep the copy leading with it.
    notCompleted: 'Assignments that exist but are not finished — a review still open, or actions not yet submitted. Unlike Missing, the work has been created; someone can act on it. Counted against the expectation of its own review type. Work left incomplete when its session closed stays counted here, since it exists and was never completed. Click a business unit\'s Open count to see how far along that work is.',
    completed: 'Expected assignments satisfied: every step done — all reviews in, actions submitted — credited against the expectation of that assignment\'s own review type. Doing two sequential where one sequential and one parallel are expected satisfies one, not two; the extra shows as surplus.',
    surplus: 'Assignments that count toward no target, so they show in neither Completed nor Missing. Either the person already had as many of that review type as the policy expects — a second sequential where one is expected — or the assignment has no review type yet because its reviewers were never set up. Real work either way, shown so it is never silently discarded.',
    noPolicy: 'This cluster has no Assignment Completion Policy for the period, so nothing is expected of it and no percentage can be calculated. Set one under Feedback Sessions → Assignment Completion Policies.',
    missing: 'Expected work with no assignment created at all. A setup gap rather than a delay — nobody can finish these until they are published.',
    skipped: `${skippedSentence} They sit outside every other column: no expectation is set for them and their assignments are not counted either way. Shown so a short headcount explains itself.`,
    completionRate: 'Completed measured against Expected — not against what was created — so a business unit that never set up its assignments reads low instead of perfect. Shown as — when nothing is expected.',
  };
};

/**
 * ADR-108: "A, B and C" — beyond three, "A, B, C and N more" (the data notes'
 * unit-naming rule, reused for engagement models).
 */
export const formatNames = (names = []) => {
  if (names.length === 0) {
    return '';
  }
  if (names.length <= 3) {
    return names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
};

/**
 * ADR-108: the two reasons a person can be Skipped, in the order the
 * eligibility chain evaluates them (exclusion before tenure; leavers and
 * hire-date anomalies are decided earlier and never reach Skipped — product,
 * 2026-09-17, amended the same day). Keys mirror `EXCLUSION_REASONS` in the
 * API's report service.
 */
export const SKIPPED_REASONS = {
  ENGAGEMENT_MODEL: 'engagementModel',
  NEW_JOINER: 'newJoiner',
};

/** Tag text for a skipped person on the BU detail page. */
export const getSkippedReasonLabel = (person) => (
  person?.skipReason === SKIPPED_REASONS.ENGAGEMENT_MODEL
    ? (person.engagementModel?.name || 'Excluded engagement model')
    : 'New joiner'
);

/**
 * One line explaining a Skipped count from its breakdown: "12 not measured:
 * 4 new joiners · 8 on excluded engagement models". Null when there is no
 * breakdown to show (older payload, or zero).
 */
export const describeSkippedBreakdown = (count, breakdown) => {
  if (!count || !breakdown) {
    return null;
  }
  const newJoiners = breakdown[SKIPPED_REASONS.NEW_JOINER] || 0;
  const excluded = breakdown[SKIPPED_REASONS.ENGAGEMENT_MODEL] || 0;
  const parts = [];
  if (newJoiners > 0) {
    parts.push(`${newJoiners} new ${newJoiners === 1 ? 'joiner' : 'joiners'}`);
  }
  if (excluded > 0) {
    parts.push(`${excluded} on ${excluded === 1 ? 'an excluded engagement model' : 'excluded engagement models'}`);
  }
  return `${count} not measured: ${parts.join(' · ')}`;
};
